import { CardNumberElement } from '@stripe/react-stripe-js'
import type { SourceCreateParams, Stripe, StripeElements } from '@stripe/stripe-js'
import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { PostOrgPaymentsCreditCardsApiArg } from '@/__generated/es-api'
import { createNotification } from '@/app/module/notifications/store/actions'
import { ERROR_SAVE_CARD_SERVER, SUCCESS_SAVE_CARD_SERVER } from '@/app/module/payments/definitions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

export const useSaveCreditCard = () => {
  const dispatch = useAppDispatch()
  const orgId = useSelector(selectOrgId)
  const [isLoading, setLoading] = useState(false)

  const [mutate, res] = esApi.endpoints.postOrgPaymentsCreditCards.useMutation()

  const saveCard = useCallback(
    async (stripe: Stripe, elements: StripeElements, owner: SourceCreateParams.Owner) => {
      setLoading(true)
      try {
        const card = elements.getElement(CardNumberElement)
        if (!card) {
          dispatch(
            createNotification({
              'save-card': { type: 'error', message: ERROR_SAVE_CARD_SERVER },
            }),
          )
          return false
        }

        const result = await stripe.createSource(card, {
          type: 'card',
          usage: 'reusable',
          currency: 'usd',
          owner,
        })

        if (!result || result.error) {
          dispatch(
            createNotification({
              'save-card': { type: 'error', message: ERROR_SAVE_CARD_SERVER },
            }),
          )
          return false
        }

        const { source } = result
        if (!source.card) {
          dispatch(
            createNotification({
              'save-card': { type: 'error', message: ERROR_SAVE_CARD_SERVER },
            }),
          )
          return false
        }

        const label = `${source.card.brand} card: **** **** **** ${source.card.last4}`

        await mutate({
          orgId: orgId.toString(),
          body: { label, stripeSourceId: source.id } as PostOrgPaymentsCreditCardsApiArg['body'],
        }).unwrap()
        dispatch(
          createNotification({
            'save-card': {
              type: 'success',
              message: SUCCESS_SAVE_CARD_SERVER,
            },
          }),
        )
        return true
      } catch {
        dispatch(
          createNotification({
            'save-card': {
              type: 'error',
              message: ERROR_SAVE_CARD_SERVER,
            },
          }),
        )
        return false
      } finally {
        setLoading(false)
      }
    },
    [orgId, dispatch, mutate],
  )

  return [
    saveCard,
    {
      ...res,
      isLoading,
    },
  ] as const
}
